import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { toast } from "sonner";
import { errorMessage } from "../api/client";
import { applyTheme, currentTheme, saveTheme, THEMES, type Theme } from "../lib/theme";

const META: Record<Theme, { label: string; swatch: [string, string, string] }> = {
  light: { label: "Light", swatch: ["#f5f5f7", "#ffffff", "#d01700"] },
  red: { label: "Red", swatch: ["#160403", "#3e0e0b", "#ff1d00"] },
  dark: { label: "Dark", swatch: ["#07080b", "#1c1f28", "#ff1d00"] },
};

export function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>(currentTheme);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  useEffect(() => {
    const on = (e: Event) => setTheme((e as CustomEvent<Theme>).detail);
    window.addEventListener("gdt-theme", on);
    return () => window.removeEventListener("gdt-theme", on);
  }, []);
  const choose = async (t: Theme) => {
    const before = theme;
    setTheme(t);
    applyTheme(t);
    try { await saveTheme(t); } catch (e) { toast.error(`Couldn't save your theme: ${errorMessage(e)}`); if (before !== t) { setTheme(before); applyTheme(before); } }
  };
  const onKey = (e: KeyboardEvent, i: number) => {
    const d = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const next = THEMES[(i + d + THEMES.length) % THEMES.length];
    void choose(next);
    refs.current[next]?.focus();
  };
  return (
    <section className="glass rounded-[20px] p-6" aria-labelledby="appearance-heading" data-testid="appearance-section">
      <h2 id="appearance-heading" className="text-lg font-medium text-white">Appearance</h2>
      <p className="mt-1 text-sm text-zinc-400">Applies right away and follows you to any device you sign in on.</p>
      <div role="radiogroup" aria-labelledby="appearance-heading" className="mt-4 flex flex-wrap gap-3" data-testid="theme-switcher">
        {THEMES.map((t, i) => {
          const on = t === theme;
          const [bg, card, accent] = META[t].swatch;
          return (
            <button key={t} ref={(el) => { refs.current[t] = el; }} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
              onClick={() => void choose(t)} onKeyDown={(e) => onKey(e, i)} data-testid={`theme-option-${t}`}
              className={`flex items-center gap-3 rounded-[14px] border px-3 py-2.5 text-sm font-medium transition-[border-color,box-shadow,background-color] ${on ? "border-signal bg-signal/10 text-white shadow-[0_0_0_1px_rgba(255,29,0,0.35)]" : "border-white/15 bg-white/[0.04] text-zinc-300 hover:border-white/30"}`}>
              <span aria-hidden className="relative h-8 w-12 overflow-hidden rounded-md border border-black/20" style={{ background: bg }}>
                <span className="absolute bottom-1 left-1 h-3 w-7 rounded-sm" style={{ background: card }} />
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full" style={{ background: accent }} />
              </span>
              {META[t].label}
            </button>
          );
        })}
      </div>
    </section>
  );
}
